/**
 * Property Requests Management Page
 * Admin page for managing lease and buy requests
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "../../components/ui/Button";
import ConfirmationModal from "../../components/ui/ConfirmationModal";
import { useToast } from "../../contexts/ToastContext";
import {
	useListPropertyRequestsQuery,
	useUpdatePropertyRequestMutation,
	type PropertyRequest,
} from "../../store/api/propertyRequestsApi";
import PropertyRequestDetailModal from "./components/PropertyRequestDetailModal";
import PropertyRequestFilters from "./components/PropertyRequestFilters";
import PropertyRequestTable from "./components/PropertyRequestTable";

const PropertyRequestsManagement: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { success, error: showError } = useToast();
	const [page, setPage] = useState(1);
	const [filters, setFilters] = useState({
		status: "",
		request_type: "",
		search: "",
	});
	const [selectedRequest, setSelectedRequest] =
		useState<PropertyRequest | null>(null);
	const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
	const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
	const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
	const [requestToAction, setRequestToAction] =
		useState<PropertyRequest | null>(null);

	const {
		data: requestsData,
		isLoading,
		error,
		refetch,
	} = useListPropertyRequestsQuery({
		page,
		page_size: 20,
		status: filters.status || undefined,
		request_type: filters.request_type || undefined,
		search: filters.search || undefined,
		ordering: "-created_at",
	});

	const [updatePropertyRequest, { isLoading: isUpdating }] =
		useUpdatePropertyRequestMutation();

	// Check for request_id in URL params (from notification link)
	useEffect(() => {
		const requestId = searchParams.get("request_id");
		if (requestId && requestsData?.results) {
			const request = requestsData.results.find((r) => r.id === requestId);
			if (request) {
				setSelectedRequest(request);
				setIsDetailModalOpen(true);
				// Clean up URL
				navigate("/property-requests-management", { replace: true });
			}
		}
	}, [searchParams, requestsData, navigate]);

	const handleFilterChange = (newFilters: {
		status?: string;
		request_type?: string;
		search?: string;
	}) => {
		setFilters((prev) => ({ ...prev, ...newFilters }));
		setPage(1);
	};

	const handleRequestClick = (request: PropertyRequest) => {
		setSelectedRequest(request);
		setIsDetailModalOpen(true);
	};

	const handleAcceptClick = (request: PropertyRequest) => {
		setRequestToAction(request);
		setIsAcceptModalOpen(true);
	};

	const handleDeclineClick = (request: PropertyRequest) => {
		setRequestToAction(request);
		setIsDeclineModalOpen(true);
	};

	const handleConfirmAccept = async () => {
		if (!requestToAction) return;

		try {
			const result = await updatePropertyRequest({
				id: requestToAction.id,
				data: { status: "accepted" },
			}).unwrap();

			success("Request accepted successfully! Redirecting to conversation...");
			setIsAcceptModalOpen(false);
			setRequestToAction(null);
			refetch();
			if (selectedRequest?.id === requestToAction.id) {
				setIsDetailModalOpen(false);
			}

			// Redirect to the conversation if one was created
			if (result.conversation_id) {
				// Give a brief moment for the success message to show
				setTimeout(() => {
					navigate(`/messages?conversation=${result.conversation_id}`);
				}, 1000);
			}
		} catch (err: any) {
			showError(
				err?.data?.detail || "Failed to accept request. Please try again.",
			);
		}
	};

	const handleConfirmDecline = async () => {
		if (!requestToAction) return;

		try {
			const result = await updatePropertyRequest({
				id: requestToAction.id,
				data: { status: "declined" },
			}).unwrap();

			success("Request declined successfully! Redirecting to conversation...");
			setIsDeclineModalOpen(false);
			setRequestToAction(null);
			refetch();
			if (selectedRequest?.id === requestToAction.id) {
				setIsDetailModalOpen(false);
			}

			// Redirect to the conversation if one was created
			if (result.conversation_id) {
				// Give a brief moment for the success message to show
				setTimeout(() => {
					navigate(`/messages?conversation=${result.conversation_id}`);
				}, 1000);
			}
		} catch (err: any) {
			showError(
				err?.data?.detail || "Failed to decline request. Please try again.",
			);
		}
	};

	return (
		<div className="w-full">
			<div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
				{/* Page Header */}
				<div className="mb-6">
					<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
						<div>
							<h1 className="text-2xl sm:text-3xl font-bold text-foreground">
								Property Requests
							</h1>
							<p className="text-muted-foreground mt-1">
								Manage lease and buy requests from potential tenants and buyers
							</p>
						</div>
					</div>
				</div>

				{/* Filters */}
				<PropertyRequestFilters
					filters={filters}
					onFilterChange={handleFilterChange}
				/>

				{/* Requests Table */}
				<div className="mt-6">
					<PropertyRequestTable
						requests={requestsData?.results || []}
						isLoading={isLoading}
						onRequestClick={handleRequestClick}
						onAccept={handleAcceptClick}
						onDecline={handleDeclineClick}
					/>
				</div>

				{/* Pagination */}
				{requestsData && requestsData.count > 0 && (
					<div className="mt-6 flex items-center justify-between">
						<p className="text-sm text-muted-foreground">
							Showing {(page - 1) * 20 + 1} to{" "}
							{Math.min(page * 20, requestsData.count)} of {requestsData.count}{" "}
							requests
						</p>
						<div className="flex items-center space-x-2">
							<Button
								variant="outline"
								size="sm"
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={page === 1 || isLoading}
							>
								Previous
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setPage((p) => p + 1)}
								disabled={
									!requestsData.next ||
									isLoading ||
									page * 20 >= requestsData.count
								}
							>
								Next
							</Button>
						</div>
					</div>
				)}

				{/* Detail Modal */}
				{selectedRequest && (
					<PropertyRequestDetailModal
						request={selectedRequest}
						isOpen={isDetailModalOpen}
						onClose={() => {
							setIsDetailModalOpen(false);
							setSelectedRequest(null);
						}}
						onAccept={() => handleAcceptClick(selectedRequest)}
						onDecline={() => handleDeclineClick(selectedRequest)}
					/>
				)}

				{/* Accept Confirmation Modal */}
				<ConfirmationModal
					isOpen={isAcceptModalOpen}
					onClose={() => {
						setIsAcceptModalOpen(false);
						setRequestToAction(null);
					}}
					onConfirm={handleConfirmAccept}
					title="Accept Request"
					message={`Are you sure you want to accept this ${requestToAction?.request_type} request? This will proceed to the next step in the process.`}
					confirmButtonText="Accept"
					isLoading={isUpdating}
				/>

				{/* Decline Confirmation Modal */}
				<ConfirmationModal
					isOpen={isDeclineModalOpen}
					onClose={() => {
						setIsDeclineModalOpen(false);
						setRequestToAction(null);
					}}
					onConfirm={handleConfirmDecline}
					title="Decline Request"
					message={`Are you sure you want to decline this ${requestToAction?.request_type} request? The user account will not be created.`}
					confirmButtonText="Decline"
					isDestructive={true}
					isLoading={isUpdating}
				/>
			</div>
		</div>
	);
};

export default PropertyRequestsManagement;
